import { addItems } from '@theme/oracura-cart';

const bound = new WeakSet();
let toastTimer;
function feedback(message) {
  let toast = document.querySelector('[data-oracura-cart-feedback]');
  if (!toast) {
    toast = document.createElement('p');
    toast.className = 'oracura-cart-feedback';
    toast.dataset.oracuraCartFeedback = '';
    toast.setAttribute('role', 'status');
  }
  // A modal dialog (the video player) sits in the browser's top layer, above
  // any z-index, so a message on the page behind it was never seen. While one
  // is open the message goes inside it.
  const host = document.querySelector('dialog[open]') || document.body;
  if (toast.parentElement !== host) host.append(toast);
  toast.textContent = message;
  toast.hidden = false;
  // Above GoKwik's cart and its overlay (z-index 9999998-9999999), or a
  // message about the cart sits dimmed underneath the cart itself.
  toast.style.zIndex = '10000002';
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toast.hidden = true; }, 5000);
}

// GoKwik opens its cart drawer on every add request it sees, refused ones
// included, so a failed add slid an empty "Your Cart (0 items)" over the
// message saying it had failed. When an add fails, the drawer that came up
// for it is closed again so the message is what the shopper sees.
function closeCartAfterFailedAdd() {
  let tries = 0;
  const check = () => {
    const open = document.querySelector('shadow-dom-container')?.shadowRoot?.querySelector('.sidecart');
    if (open && typeof window.closeGokwikSideCart === 'function') {
      window.closeGokwikSideCart();
      return;
    }
    if (++tries < 10) setTimeout(check, 150);
  };
  check();
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-plp-add], [data-add-variant]');
  if (!button) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (button.disabled) return;
  const id = button.dataset.plpAdd || button.dataset.addVariant;
  if (!id) return;
  const text = button.querySelector('.oracura-pcard__add-text, .oracura-mcard__add-text, .oracura-related__add-text, .oracura-video-cards__viewer-add-text, [data-card-add-text]');
  const original = text?.textContent;
  const variantId = id;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  try {
    const result = await addItems([{ id: Number(id), quantity: 1 }], button);
    if (result?.cancelled) return;
    if (text) text.textContent = window.OracuraLabels?.added || 'Added';
    button.classList.add('is-added');
    // Added from inside a modal (the video player): the cart that opens for
    // the add sits under the top layer, so it stayed hidden until the player
    // was closed by hand. The player steps aside so the cart is what shows.
    const dialog = button.closest('dialog[open]');
    if (dialog) dialog.close();
  } catch (error) {
    feedback(error.message);
    closeCartAfterFailedAdd();
  }
  finally {
    button.removeAttribute('aria-busy');
    setTimeout(() => {
      if ((button.dataset.plpAdd || button.dataset.addVariant) !== variantId) return;
      button.disabled = false;
      button.classList.remove('is-added');
      if (text) text.textContent = original;
    }, 1200);
  }
}, true);

function init(scope = document) {
  scope.querySelectorAll('.oracura-faq').forEach(root => {
    if (bound.has(root)) return;
    bound.add(root);
    const tabs = [...root.querySelectorAll('[data-faq-tab]')];
    const panels = [...root.querySelectorAll('[data-faq-panel]')];
    if (!tabs.length) return;
    const select = index => {
      tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        tab.classList.toggle('is-active', active);
      });
      panels.forEach(panel => { panel.hidden = panel.dataset.faqPanel !== tabs[index].dataset.faqTab; });
      const strip = root.querySelector('.oracura-faq__tabs');
      const tabBox = tabs[index].getBoundingClientRect();
      const stripBox = strip.getBoundingClientRect();
      const delta = tabBox.left < stripBox.left ? tabBox.left - stripBox.left : tabBox.right > stripBox.right ? tabBox.right - stripBox.right : 0;
      if (delta) strip.scrollBy({ left: delta, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches || window.OracuraMotion?.paused ? 'instant' : 'smooth' });
    };
    root.addEventListener('click', event => {
      const tab = event.target.closest('[data-faq-tab]');
      if (tab) select(tabs.indexOf(tab));
    });
    root.addEventListener('keydown', event => {
      const index = tabs.indexOf(event.target);
      if (index < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      select(next); tabs[next].focus();
    });
    const originalTabs = tabs.slice();
    let arranged = false;
    // The phone order below (usage, charging, maintenance, warranty) is the
    // product page's. The store-wide FAQ page (#oracura-faq) keeps the order its
    // questions were written in, so on a phone it opened on "Charging & Care"
    // instead of its first category.
    const mobile = matchMedia(root.id === 'oracura-faq' ? 'not all' : '(max-width: 760px)');
    const arrange = () => {
      const selected = arranged ? tabs.find(tab => tab.getAttribute('aria-selected') === 'true') : null;
      // Each place in the order answers to every name the products give it:
      // KSB100 calls its charging tab "Battery & Power", OC450 "Power", and
      // matching "charging" alone sent both to the end of the row.
      const order = [['usage'], ['charging', 'battery', 'power'], ['maintenance'], ['warranty']];
      const rank = tab => {
        const label = tab.textContent.trim().toLowerCase();
        const index = order.findIndex(names => names.some(name => label.includes(name)));
        return index < 0 ? order.length : index;
      };
      tabs.splice(0, tabs.length, ...(mobile.matches ? originalTabs.slice().sort((a, b) => rank(a) - rank(b)) : originalTabs));
      tabs.forEach(tab => root.querySelector('.oracura-faq__tabs').append(tab));
      select(selected ? tabs.indexOf(selected) : 0);
      arranged = true;
    };
    mobile.addEventListener('change', arrange);
    root.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => mobile.removeEventListener('change', arrange), { once: true });
    arrange();
  });
  scope.querySelectorAll('.oracura-ptabs').forEach(nav => {
    if (bound.has(nav)) return;
    bound.add(nav);
    const links = [...nav.querySelectorAll('a[href^="#"]')].filter(link => {
      const target = document.getElementById(link.hash.slice(1));
      if (!target) { link.hidden = true; return false; }
      return true;
    });
    if (!links.length) return;
    const controller = new AbortController();
    let frame = 0;
    const update = () => {
      frame = 0;
      const boundary = Math.max(120, nav.getBoundingClientRect().bottom + 24);
      let active = links[0], closest = -Infinity;
      links.forEach(link => {
        const target = document.getElementById(link.hash.slice(1));
        if (!target?.getClientRects().length) return;
        const top = target.getBoundingClientRect().top;
        if (top <= boundary && top > closest) { active = link; closest = top; }
      });
      links.forEach(link => {
        if (link === active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    document.addEventListener('scroll', schedule, { capture: true, passive: true, signal: controller.signal });
    window.addEventListener('resize', schedule, { passive: true, signal: controller.signal });
    nav.addEventListener('click', event => {
      const link = event.target.closest('a');
      if (!links.includes(link) || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const target = document.getElementById(link.hash.slice(1));
      if (!target) return;
      event.preventDefault();
      let scroller = nav.parentElement;
      while (scroller && !(/auto|scroll/.test(getComputedStyle(scroller).overflowY) && scroller.scrollHeight > scroller.clientHeight)) {
        scroller = scroller.parentElement;
      }
      scroller ||= document.scrollingElement;
      const section = nav.closest('.shopify-section');
      const stickyTop = parseFloat(getComputedStyle(section || nav).top) || 0;
      const offset = stickyTop + nav.getBoundingClientRect().height + 12;
      scroller.scrollTo({
        top: scroller.scrollTop + target.getBoundingClientRect().top - offset,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
      history.replaceState(null, '', link.hash);
      schedule();
    }, { signal: controller.signal });
    update();
    const section = nav.closest('.shopify-section');
    section?.addEventListener('shopify:section:unload', () => { controller.abort(); cancelAnimationFrame(frame); }, { once: true });
  });
}

// Keyboard scrolling on desktop. From 990px the page scrolls inside
// .page-wrapper (base.css), not the document, so with focus on <body> - every
// page load, before anything is clicked - Page Down, Space and the arrows
// scrolled nothing. The keys are applied to the wrapper here instead. Focus is
// never moved, so the skip link stays the first Tab stop and an input that has
// focus keeps its own keys. Only when the wrapper really is the scroller and no
// drawer or dialog has locked the page.
const SCROLL_KEYS = ['PageDown', 'PageUp', ' ', 'ArrowDown', 'ArrowUp', 'Home', 'End'];
document.addEventListener('keydown', (event) => {
  if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || !SCROLL_KEYS.includes(event.key)) return;
  const active = document.activeElement;
  if (active && active !== document.body && active !== document.documentElement) return;
  const root = document.documentElement;
  if (root.hasAttribute('scroll-lock') || root.classList.contains('shop-drawer-open')) return;
  if (document.querySelector('shadow-dom-container')?.shadowRoot?.querySelector('.sidecart')) return;
  const wrapper = document.querySelector('.page-wrapper');
  if (!wrapper || !/auto|scroll/.test(getComputedStyle(wrapper).overflowY) || wrapper.scrollHeight <= wrapper.clientHeight) return;
  const page = Math.max(wrapper.clientHeight - 64, 40);
  const step = { PageDown: page, PageUp: -page, ' ': event.shiftKey ? -page : page, ArrowDown: 48, ArrowUp: -48 }[event.key];
  event.preventDefault();
  const instant = event.repeat || matchMedia('(prefers-reduced-motion: reduce)').matches;
  const top = event.key === 'Home' ? 0 : event.key === 'End' ? wrapper.scrollHeight : wrapper.scrollTop + step;
  wrapper.scrollTo({ top, behavior: instant ? 'instant' : 'smooth' });
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
else init();
document.addEventListener('shopify:section:load', event => init(event.target));
